import React from 'react';
import { Search, Download, FileText } from 'lucide-react';

/**
 * 토스페이먼츠 스타일 관리 페이지 (테이블 + 상세 패널)
 * example.js ManagementPage 패턴
 */
function ManagementPage({
  title,
  subTabs,
  filters,
  tableHeaders,
  data = [],
  renderRow,
  getRowClassName,
  detailTitle = '상세 내역',
  detailButtonLabel,
  detailButtonIcon,
  detailButtonSmall = false,
  detailButtonDisabled = false,
  excelButtonClassName,
  excelButtonTooltipProps,
  extraButtons,
  leftActions,
  renderDetail,
  selectedItem,
  onSelectItem,
  onExcelDownload,
  onDetailButtonClick,
  hideSearch = false,
  noPadding = false,
  hideExcelButton = false,
  hideDetail = false,
  emptyStateIcon,
  tableContainerStyle,
  scrollBodyOnly = false,
  tableCols,
  onTableScroll,
  infiniteScrollRef,
}) {
  const handleExcelDownload = () => {
    if (onExcelDownload) onExcelDownload();
    else if (typeof onExcelDownload === 'undefined') {
      // Toast will be called from parent
    }
  };

  return (
    <div className={`flex-1 flex flex-col overflow-hidden min-h-0 bg-white ${noPadding ? 'py-0 px-0' : 'py-4 px-8'}`}>
      {subTabs && (
        <div className="flex items-end gap-6 shrink-0 pb-2 border-b border-transparent">
          {subTabs.map((tab) => (
            <button
              key={tab.label}
              onClick={tab.onClick}
              className={`text-[18px] font-extrabold pb-2 border-b-2 transition-all ${
                tab.active ? 'text-[#191f28] border-[#191f28]' : 'text-[#adb5bd] border-transparent hover:text-[#4e5968]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <div className="flex-1 flex flex-col gap-3 overflow-hidden min-h-0">
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {!hideSearch && (
            <div className="w-[250px] h-[42px] flex items-center bg-[#f2f4f6] px-3 rounded-lg border border-transparent focus-within:border-[#0064ff] transition-all shrink-0">
              <input type="text" placeholder="검색어 입력" className="bg-transparent border-none outline-none w-full text-[16px]" />
              <Search size={16} className="text-[#adb5bd] shrink-0" />
            </div>
          )}
          {filters}
        </div>
        <div className={`flex-1 flex gap-6 overflow-hidden min-h-0 ${hideDetail ? '' : 'pb-8'}`}>
          <div className={`${hideDetail ? 'flex-1' : 'flex-[6]'} flex flex-col overflow-hidden min-h-0 ${hideDetail ? '' : 'min-w-[720px]'} bg-white rounded-2xl border border-[#e0e0e0] shadow-md`}>
            <div className="px-3 py-2 border-b border-[#f2f4f6] flex items-center bg-[#fcfdfe] shrink-0">
              <div className="flex gap-4 flex-1">{leftActions}</div>
              <div className="flex gap-3 flex-1 justify-end">
                {extraButtons}
                {!hideExcelButton && (
                  excelButtonTooltipProps ? (
                    <span
                      data-tooltip-id={excelButtonTooltipProps['data-tooltip-id']}
                      data-tooltip-html={excelButtonTooltipProps['data-tooltip-html'] ?? excelButtonTooltipProps['data-tooltip-content']}
                      onMouseEnter={excelButtonTooltipProps.onMouseEnter}
                      onMouseLeave={excelButtonTooltipProps.onMouseLeave}
                      className="inline-flex cursor-help"
                    >
                      <button
                        onClick={handleExcelDownload}
                        className={
                          excelButtonClassName ||
                          'flex items-center gap-2 px-4 py-2 bg-[#191f28] text-white rounded-lg font-bold text-[16px] hover:bg-black transition-all'
                        }
                      >
                        <Download size={14} /> 엑셀다운로드
                      </button>
                    </span>
                  ) : (
                    <button
                      onClick={handleExcelDownload}
                      className={
                        excelButtonClassName ||
                        'flex items-center gap-2 px-4 py-2 bg-[#191f28] text-white rounded-lg font-bold text-[16px] hover:bg-black transition-all'
                      }
                    >
                      <Download size={14} /> 엑셀다운로드
                    </button>
                  )
                )}
              </div>
            </div>
            {scrollBodyOnly ? (
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="shrink-0 overflow-hidden pr-[17px] border-b border-[#f2f4f6] [scrollbar-gutter:stable]">
                  <div className="overflow-y-scroll" style={{ scrollbarGutter: 'stable', marginRight: -17 }}>
                    <table className="w-full text-left text-[14px] table-fixed" style={{ tableLayout: 'fixed' }}>
                      {tableCols && <colgroup>{tableCols}</colgroup>}
                      <thead className="bg-[#f9fafb] text-[#8b95a1] font-bold text-[14px]">
                        <tr>{tableHeaders}</tr>
                      </thead>
                    </table>
                  </div>
                </div>
                <div
                  className="flex-1 min-h-0 overflow-y-auto custom-scrollbar"
                  style={tableContainerStyle}
                  onScroll={onTableScroll}
                >
                  <table className="w-full text-left text-[14px] table-fixed" style={{ tableLayout: 'fixed' }}>
                    {tableCols && <colgroup>{tableCols}</colgroup>}
                    <tbody className="divide-y divide-[#f2f4f6] [&>tr:last-child>td]:border-b [&>tr:last-child>td]:border-[#f2f4f6]">
                      {!data?.length ? (
                        <tr>
                          <td colSpan={20} className="px-4 py-12 text-center text-[15px] font-medium text-[#8b95a1]">
                            데이터가 없습니다.
                          </td>
                        </tr>
                      ) : (
                        data.map((item) => (
                          <tr
                            key={item.id}
                            onClick={() => onSelectItem && onSelectItem(item)}
                            className={`hover:bg-[#f9fafb] ${onSelectItem ? 'cursor-pointer' : ''} transition-all h-[33px] ${
                              selectedItem != null && selectedItem.id === item.id ? 'bg-[#e7f5ff]' : ''
                            } ${getRowClassName ? getRowClassName(item) : ''}`}
                          >
                            {renderRow(item)}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {infiniteScrollRef && <div ref={infiniteScrollRef} className="h-1 min-h-[1px]" aria-hidden="true" />}
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0" style={tableContainerStyle} onScroll={onTableScroll}>
                <table className="w-full text-left text-[14px]">
                  <thead className="bg-[#f9fafb] text-[#8b95a1] font-bold text-[14px] sticky top-0 z-10 border-b border-[#f2f4f6]">
                    <tr>{tableHeaders}</tr>
                  </thead>
                  <tbody className="divide-y divide-[#f2f4f6] [&>tr:last-child>td]:border-b [&>tr:last-child>td]:border-[#f2f4f6]">
                    {!data?.length ? (
                      <tr>
                        <td colSpan={20} className="px-4 py-12 text-center text-[15px] font-medium text-[#8b95a1]">
                          데이터가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      data.map((item) => (
                        <tr
                          key={item.id}
                          onClick={() => onSelectItem && onSelectItem(item)}
                          className={`hover:bg-[#f9fafb] ${onSelectItem ? 'cursor-pointer' : ''} transition-all h-[33px] ${
                            selectedItem != null && selectedItem.id === item.id ? 'bg-[#e7f5ff]' : ''
                          } ${getRowClassName ? getRowClassName(item) : ''}`}
                        >
                          {renderRow(item)}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {infiniteScrollRef && <div ref={infiniteScrollRef} className="h-1 min-h-[1px]" aria-hidden="true" />}
              </div>
            )}
          </div>
          {!hideDetail && (
            <div className="flex-[4] flex flex-col overflow-hidden min-h-0 min-w-[380px] bg-white rounded-2xl border border-[#e0e0e0] shadow-md">
              <div className="px-4 h-[58px] border-b border-[#f2f4f6] flex justify-between items-center bg-[#fcfdfe] shrink-0">
                <h3 className="text-[18px] font-extrabold text-[#191f28]">{detailTitle}</h3>
                {detailButtonLabel && (
                  <button
                    onClick={onDetailButtonClick}
                    disabled={detailButtonDisabled}
                    className={`bg-[#0054a6] text-white rounded-lg font-bold flex items-center gap-2 hover:bg-[#004080] transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                      detailButtonSmall ? 'px-3 py-1.5 text-[14px]' : 'px-4 py-2 text-[16px]'
                    }`}
                  >
                    {detailButtonIcon ?? <FileText size={14} />} {detailButtonLabel}
                  </button>
                )}
              </div>
              <div className="flex-1 p-6 overflow-y-auto custom-scrollbar min-h-0">
                {!selectedItem ? (
                  <div className="h-full flex flex-col items-center justify-center text-[#adb5bd] space-y-4">
                    <div className="w-16 h-16 bg-[#f2f4f6] rounded-full flex items-center justify-center">
                      {emptyStateIcon ?? <Search size={32} className="opacity-20" />}
                    </div>
                    <p className="font-bold text-center text-[16px]">
                      목록에서 항목을 선택하여<br />상세정보를 확인해 주세요.
                    </p>
                  </div>
                ) : (
                  renderDetail && renderDetail(selectedItem)
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManagementPage;
